/**
 * SignalAlerteModal — Volet "Signaler une alerte" avec 3 sous-onglets :
 *   • Alerte terrain (issue rapide)
 *   • Demande (kind = 'demande')
 *   • Problématique (kind = 'probleme')
 * Inclut un upload photo drag & drop (Object Storage) pour joindre une preuve visuelle.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, AlertTriangle, MessageCircle, HelpCircle, Camera, ImagePlus, Trash2 } from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const KINDS = [
  { k:"alerte",     label:"Alerte",        icon: AlertTriangle, hint:"Signaler un incident critique" },
  { k:"demande",    label:"Demande",       icon: HelpCircle,    hint:"Question ou demande d'infos" },
  { k:"probleme",   label:"Problématique", icon: MessageCircle, hint:"Difficulté récurrente" },
];
const PRIOS = [{v:"critical",l:"Critique"},{v:"high",l:"Haute"},{v:"medium",l:"Moyenne"},{v:"low",l:"Basse"}];

export default function SignalAlerteModal({ open, onClose, onCreated, stores = [] }) {
  const [kind, setKind] = useState("alerte");
  const [f, setF] = useState({ title:"", description:"", priority:"medium", store_id:"", photos:[] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (open) { setKind("alerte"); setF({ title:"", description:"", priority:"medium", store_id:"", photos:[] }); }
  }, [open]);
  if (!open) return null;

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
        urls.push(`${api.defaults.baseURL}/files/${data.path}`);
      }
      setF(v => ({ ...v, photos: [...v.photos, ...urls] }));
      toast.success(`${urls.length} photo${urls.length>1?"s":""} ajoutée${urls.length>1?"s":""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload photo"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Titre obligatoire"); return; }
    setSaving(true);
    try {
      await api.post("/issues", {
        title: f.title,
        description: f.description,
        priority: f.priority,
        store_id: f.store_id || null,
        photos: f.photos,
        kind: kind === "alerte" ? "probleme" : kind,
      });
      toast.success(kind==="alerte"?"Alerte signalée":kind==="demande"?"Demande créée":"Problématique créée");
      onCreated?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="signal-alerte-modal">
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-2rem)] flex flex-col">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
          <div className="w-8 h-8 rounded bg-rose-600 text-white grid place-items-center"><AlertTriangle className="w-4 h-4"/></div>
          <h3 className="font-display text-lg font-bold flex-1">Signaler une alerte</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded hover:bg-slate-100 grid place-items-center"><X className="w-4 h-4"/></button>
        </div>

        {/* 3-kind tabs */}
        <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 border-b border-slate-100">
          {KINDS.map(k => {
            const I = k.icon; const active = kind === k.k;
            return (
              <button type="button" key={k.k} onClick={()=>setKind(k.k)} data-testid={`alerte-kind-${k.k}`}
                      className={`p-3 rounded-xl border text-left transition ${active?"border-rose-500 bg-white shadow-sm":"border-transparent bg-white/60 hover:bg-white"}`}>
                <I className={`w-4 h-4 mb-1 ${active?"text-rose-600":"text-slate-500"}`}/>
                <div className={`text-[12px] font-bold ${active?"text-slate-900":"text-slate-700"}`}>{k.label}</div>
                <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{k.hint}</div>
              </button>
            );
          })}
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1">Titre *</label>
            <input required autoFocus value={f.title} onChange={e=>setF({...f,title:e.target.value})}
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="al-title"
                   placeholder={kind==="alerte"?"Ex : Frigo en panne":kind==="demande"?"Ex : Besoin d'infos formation":"Ex : Rupture récurrente saumon"}/>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1">Description</label>
            <textarea rows={3} value={f.description} onChange={e=>setF({...f,description:e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 block mb-1">Corner concerné</label>
              <SearchSelect value={f.store_id} onChange={v => setF({...f,store_id:v})} options={[...stores.slice(0,300).map(s => ({ value: s.id, label: s.canonical_name||s.name }))]} placeholder={"— Aucun —"} size="lg" menuWidth={260}/>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 block mb-1">Priorité</label>
              <SearchSelect value={f.priority} onChange={v => setF({...f,priority:v})} options={[...PRIOS.map(p => ({ value: p.v, label: p.l }))]} size="lg" menuWidth={260}/>
            </div>
          </div>

          {/* Photos upload */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1 flex items-center gap-1"><Camera className="w-3 h-3"/>Photos (preuves visuelles)</label>
            <input ref={fileInput} type="file" accept="image/*" multiple className="hidden"
                   onChange={(e)=>{ uploadFiles(e.target.files); e.target.value=""; }}
                   data-testid="al-photo-input"/>
            <div
              onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
              onDragLeave={()=>setDragOver(false)}
              onDrop={(e)=>{ e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
              onClick={()=>fileInput.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed transition p-3 flex items-center gap-3 ${dragOver?"border-rose-400 bg-rose-50":"border-slate-200 hover:border-rose-300 hover:bg-slate-50"}`}
              data-testid="al-photo-dropzone">
              <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 grid place-items-center shrink-0"><ImagePlus className="w-5 h-5"/></div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-slate-800">{uploading ? "Envoi en cours…" : "Cliquer ou glisser des photos ici"}</div>
                <div className="text-[10px] text-slate-500">JPG, PNG, WEBP — plusieurs fichiers acceptés</div>
              </div>
            </div>

            {f.photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2" data-testid="al-photo-list">
                {f.photos.map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
                    <img src={url} alt={`photo-${i}`} className="w-full h-full object-cover"/>
                    <button type="button" onClick={()=>setF(v=>({...v, photos: v.photos.filter((_,idx)=>idx!==i)}))}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-rose-600 grid place-items-center opacity-0 group-hover:opacity-100 shadow-md"
                            data-testid={`al-photo-rm-${i}`}>
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-white">Annuler</button>
          <button type="submit" disabled={saving||uploading} data-testid="al-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">{saving?"Envoi…":"Signaler"}</button>
        </div>
      </form>
    </div>
  );
}
